import { useEffect, useEffectEvent, useState } from 'react';
import { useParams } from 'react-router';
import { useLazyQuery, useMutation } from '@apollo/client/react';

import client from 'apollo/client';
import useAgent from './useAgent';
import useAnalytics from './useAnalytics';
import useTimeoutValue from './useTimeoutValue';

import { ADD_AGENT_AUDIT_LOG_FOR_APPLICATION_SSN_MUTATION } from 'mutations/addAgentAuditLog';
import { APPLICANT_META, APPLICANT_FULL_SSN } from 'queries/applicant';

import { ANALYTICS_EVENTS } from 'utilities/constants';

const useApplicantFullSsn = () => {
  const agent = useAgent();
  const { trackEvent } = useAnalytics();
  const { applicationId } = useParams();
  const [fullSsn, setFullSsn] = useState('');
  const [failureMessage, setFailureMessage] = useTimeoutValue('', 5000);

  const [addAuditLogForFullSsn, auditLogQueryStatus] = useMutation(
    ADD_AGENT_AUDIT_LOG_FOR_APPLICATION_SSN_MUTATION,
  );

  let [getFullSsn, fullSsnQuery] = useLazyQuery(APPLICANT_FULL_SSN, {
    fetchPolicy: 'network-only',
  });

  const retrieveFullSsn = async () => {
    try {
      const response = await getFullSsn({ variables: { applicationId } });
      const applicants =
        response.data.applicationV2.submission.applicants || [];
      setFullSsn(applicants[0]?.fullSsn);
    } catch {
      setFailureMessage('Failed to get full SSN.');
    }
  };

  let requestFullSsn = async () => {
    if (!fullSsn) {
      try {
        trackEvent(ANALYTICS_EVENTS.FULL_SSN_REQUESTED);
        try {
          await addAuditLogForFullSsn({
            variables: {
              applicationId,
              agentId: agent?.user?.requester_id,
              agentName: agent?.user?.name,
            },
          });
        } catch {
          setFailureMessage(
            'Failed to save audit log for full SSN request. unable to get full SSN.',
          );
          return;
        }

        retrieveFullSsn();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const reset = useEffectEvent(() => {
    setFullSsn('');
  });

  /**
   *  Handles two reset cases:
   *  1: applicationId changes
   *  2: applicant meta has been refetched
   */
  useEffect(() => {
    if (applicationId) {
      let subscription = client
        .watchQuery({
          query: APPLICANT_META,
          variables: { applicationId },
        })
        .subscribe(() => {
          reset();
        });
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [applicationId]);

  return [
    fullSsn,
    requestFullSsn,
    failureMessage,
    Object.assign({}, fullSsnQuery, auditLogQueryStatus),
  ];
};

export default useApplicantFullSsn;
